import { useState, useEffect, Fragment, useContext } from "react";
import { useNavigate } from "react-router";
import { useSignOut, useSignIn } from "react-auth-kit";
import {
  sendApiRequest,
  createQueryStringFromFilters,
} from "../../services/Base/base-service";

import ExternalFlowUploadModal from "../../components/Modals/ExternalFlowUploadModal";
import ExternalFlowUploadSignModal from "../../components/Modals/ExternalFlowUploadSignModal";
import Filters from "../../components/Header/Filters";
import UserContext from "../../store/user-context";
import ExternalFlowsTable from "../../components/Tables/ExternalFlowsTable";
import { Alert, Section, Spinner } from "design-react-kit";
import Footer from "../../components/Footer";
import ConfirmExternalFlowSendModal from "../../components/Modals/ConfirmExternalFlowSendModal";
import { closeDropdown } from "../../services/helpers";

const ExternalFlows = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [confirmSendModalOpen, setConfirmSendModalOpen] = useState(false);

  const [flow, setFlow] = useState(null);
  const [flowResults, setFlowResults] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [flowFilteredResults, setFilteredResults] = useState(null);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const getExternalFlows = (searchText = null) => {
    setError(null);
    setIsLoading(true);
    sendApiRequest(
      "ExternalFlow/GetExternalFlows",
      "GET",
      true,
      filters,
      signIn,
      signOut,
      navigate,
      createQueryStringFromFilters,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          if (searchText !== null && searchText !== "") {
            const filteredExternalFlows = response.data.flows.filter(
              (item) =>
                item.zipFileName
                  .toLowerCase()
                  .includes(searchText.toLowerCase()) ||
                item.clientCode.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredResults(filteredExternalFlows);
          } else {
            setFlowResults(response.data.flows);
            setFilteredResults(null);
          }
          setIsLoading(false);
        }
      }
    });
  };

  const getFilters = (filters) => {
    setFilters(filters);
  };

  useEffect(() => {
    if (filters !== null) {
      setTimeout(() => {
        getExternalFlows();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const uploadFlowModalOpenHandler = (flow) => {
    closeDropdown();
    setFlow(flow === undefined ? null : flow);
    setModalOpen(true);
  };

  const uploadFlowSignModalOpenHandler = (flow) => {
    closeDropdown();
    setFlow(flow === undefined ? null : flow);
    setSignModalOpen(true);
  };

  const modalCloseHandler = () => {
    setModalOpen(false);
    setFlow(null);
    getExternalFlows();
  };

  const signModalCloseHandler = () => {
    setSignModalOpen(false);
    setFlow(null);
    getExternalFlows();
  };

  const confirmSendModalCloseHandler = () => {
    setFlow(null);
    setConfirmSendModalOpen(false);
    getExternalFlows();
  };
  const confirmSendModalOpenHandler = (flow) => {
    closeDropdown();
    setFlow(flow === undefined ? null : flow);
    setConfirmSendModalOpen(true);
  };

  const authorizeForSending = (flowId) => {
    sendApiRequest(
      `ExternalFlow/AuthorizeExternalFlowForSending`,
      "POST",
      true,
      { externalFlowId: flowId },
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined && response.errors !== null) {
        setError(response.errors);
      } else {
        getExternalFlows();
        setConfirmSendModalOpen(false);
      }
    });
  };

  return (
    <Fragment>
      <Filters
        startDateLabel="Data caricamento (inizio)"
        endDateLabel="Data caricamento (fine)"
        uploadButtonFlowLabel="+ Carica file"
        applyFilterButtonLabel="Filtra"
        page="externalFlows"
        getFilters={getFilters}
        openModalHandler={uploadFlowModalOpenHandler}
      />
      <ExternalFlowUploadModal
        modalOpen={modalOpen}
        modalCloseHandler={modalCloseHandler}
        externalFlow={flow}
      />
      <ExternalFlowUploadSignModal
        modalOpen={signModalOpen}
        modalCloseHandler={signModalCloseHandler}
        externalFlow={flow}
      />
      <ConfirmExternalFlowSendModal
        confirmModalOpen={confirmSendModalOpen}
        confirmModalCloseHandler={confirmSendModalCloseHandler}
        authorizeForSending={authorizeForSending}
        isLoading={isLoading}
        error={error}
        flowId={flow?.id}
      />
      <Section>
        {error !== null && error.length !== undefined && (
          <Alert color="danger" className="mx-auto">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}
        {error === null && isLoading && (
          <p style={{ height: "50px", position: "relative" }}>
            <Spinner double active />
          </p>
        )}
        {error === null &&
          !isLoading &&
          flowResults !== null &&
          flowResults !== null && (
            <ExternalFlowsTable
              items={flowResults}
              filteredItems={flowFilteredResults}
              columns={[
                {
                  name: "Data ed ora di caricamento",
                  type: "date",
                  sortable: true,
                  value: "zipUploadDate",
                },
                {
                  name: "Nome file (flusso notifiche)",
                  type: "string",
                  sortable: true,
                  value: "zipFileName",
                },
                {
                  name: "Nome file (PDF firmati)",
                  type: "string",
                  sortable: true,
                  value: "zipWithSignatureFileName",
                },
                {
                  name: "Tipologia",
                  type: "string",
                  sortable: true,
                  value: "code",
                },
                {
                  name: "Stato",
                  type: "string",
                  sortable: false,
                  value: "status",
                },
                {
                  name: "Azioni",
                  type: "",
                  sortable: false,
                  value: "",
                },
              ]}
              uploadFlowModalOpenHandler={uploadFlowModalOpenHandler}
              uploadFlowSignModalOpenHandler={uploadFlowSignModalOpenHandler}
              confirmSendModalOpenHandler={confirmSendModalOpenHandler}
              authorizeForSending={authorizeForSending}
              getExternalFlows={getExternalFlows}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          )}
      </Section>
      <Footer />
    </Fragment>
  );
};

export default ExternalFlows;
